import fs from 'fs'
import path from 'path'

import type { SharedExtensionConfigItem, SharedExtensionDefinition } from '@shared/scheme'
import { buildServiceDefinition, getServiceConfigs } from '@flowrunner/flow-extension-runner'
import { parseJsDocToServiceJson } from '@flowrunner/jsdoc-parser'

import type { SkippedService } from '../../errors'

export interface DiscoveredService {
  /** Entry point relative to the service root — 'src/index.js', not 'services/<id>/src/index.js'. */
  serviceFilePath: string
  serviceId: string
  serviceInfo: SharedExtensionDefinition['serviceInfo']
  methodsMap: SharedExtensionDefinition['methodsMap']
  configDescriptions: SharedExtensionConfigItem[]
  /** Which authoring format the entry point turned out to be — the deploy carries it so the console can branch. */
  runnerVersion: SharedExtensionDefinition['runnerVersion']
  /** v2 only: the declared config schema, encoded. `null` for a JSDoc-parsed service. */
  configSchema: SharedExtensionDefinition['configSchema']
}

export interface DiscoveryResult {
  services: DiscoveredService[]
  /**
   * Folders under `services/` that did not become a service, and why.
   *
   * Returned rather than warned about: a folder silently missing from a deploy is the kind of thing someone
   * notices a week later, and a caller driving this from code has no console to have missed a warning on.
   */
  skipped: SkippedService[]
}

/** Scan `services/*` under the project root and build each service's definition, v2 declarative or v1 JSDoc. */
export function discoverServices(projectRoot: string = process.cwd()): DiscoveryResult {
  const servicesDir = path.resolve(projectRoot, 'services')

  if (!fs.existsSync(servicesDir)) {
    return { services: [], skipped: [] }
  }

  const serviceIds = fs
    .readdirSync(servicesDir, { withFileTypes: true })
    .filter(file => file.isDirectory())
    .map(file => file.name)

  const services: DiscoveredService[] = []
  const skipped: SkippedService[] = []

  for (const serviceId of serviceIds) {
    const serviceDir = path.resolve(servicesDir, serviceId)
    const serviceDISTEntryPoint = path.resolve(serviceDir, './dist/index.js')
    const serviceSRCEntryPoint = path.resolve(serviceDir, './src/index.js')

    let serviceFilePath = null

    try {
      if (fs.existsSync(serviceDISTEntryPoint)) {
        serviceFilePath = serviceDISTEntryPoint
      } else if (fs.existsSync(serviceSRCEntryPoint)) {
        serviceFilePath = serviceSRCEntryPoint
      }

      if (!serviceFilePath) {
        skipped.push({ serviceId, reason: 'it has neither a dist/index.js nor a src/index.js' })

        continue
      }

      // null for a v1 service, which the JSDoc parser models instead. The cast is load-bearing: the runner's
      // model mirror and @shared/scheme have drifted and are not assignable in either direction.
      const v2Definition = buildServiceDefinition(serviceId, serviceFilePath) as SharedExtensionDefinition | null

      const { methodsMap, serviceInfo } = v2Definition ?? parseJsDocToServiceJson(serviceFilePath)

      // Only files that define a service (v1: @integrationName; v2: createExtension id).
      if (!serviceInfo || !serviceInfo.serviceClassName) {
        skipped.push({ serviceId, reason: 'its entry point does not define a service' })

        continue
      }

      // A failure here shouldn't drop the whole service — record it and fall back to an empty config.
      let configDescriptions: SharedExtensionConfigItem[] = []

      try {
        configDescriptions = getServiceConfigs(serviceId, serviceFilePath)
      } catch (error) {
        skipped.push({
          serviceId,
          reason: `its config items could not be read (${ (error as Error).message }) — deployed without them`,
        })
      }

      services.push({
        // Relative to the service, because the deployed archive's root IS the service.
        serviceFilePath: path.relative(serviceDir, serviceFilePath),
        serviceId,
        serviceInfo,
        methodsMap,
        configDescriptions,
        // v2 only. The console renders a v2 config dialog from the schema itself rather than from the
        // `configDescriptions` projection, so both travel: the schema for the new path, the projection for
        // every consumer that predates it.
        runnerVersion: v2Definition ? 'v2' : 'v1',
        configSchema : v2Definition?.configSchema ?? null,
      })
    } catch (error) {
      // Anything that throws while loading the entry point: a syntax error, a missing require, a module that
      // runs something at import time. The reason is the only clue the author gets about why their service
      // is not in the deploy, so it travels rather than being swallowed.
      skipped.push({ serviceId, reason: (error as Error).message })
    }
  }

  return { services, skipped }
}
